import DropDownList from "@/components/Dropdown";
import { ListStatus, ListUserRoles } from "@/constants/default";
import { RootState } from "@/store";
import { emailRegex } from "@/utils/regex";
import { Control, Controller, FieldErrors, UseFormRegister, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export interface UserFieldForm {
    name: string
    email: string
    status: boolean
    role: number
    password: string
    retype_password: string
    discord_user_id?: string
    api_only?: boolean
}

interface InformationProp {
    register: UseFormRegister<UserFieldForm>
    errors: FieldErrors<UserFieldForm>
    control: Control<UserFieldForm, any>
    watch: UseFormWatch<UserFieldForm>
    isEdit?: boolean
    editingUserRole?: number
    editingUserId?: string
}

export default function Information({register, control, errors, watch, isEdit = false, editingUserRole, editingUserId}: InformationProp) {
    const {t} = useTranslation()
    const { auth } = useSelector((state: RootState) => state.auth)
    const listRoles = ListUserRoles().filter(item => item.value > auth.role)
    const listStatus = ListStatus();

    // Editing your own account is always allowed, even at the same/higher role
    const isSelfEdit = editingUserId !== undefined && String(auth.id) === String(editingUserId);

    // Disable role dropdown if Admin is editing another Admin or SuperAdmin
    const isRoleDisabled = isEdit && !isSelfEdit && editingUserRole !== undefined && auth.role >= editingUserRole;

    return (
        <>
            <div className="mb-3">
                <label className="mb-1 block dark:text-white">
                    {t("user.role")}
                </label>
                <Controller
                    name="role"
                    control={control}
                    defaultValue={listRoles[0].value}
                    render={({field: {onChange, value}}) => <DropDownList selected={value} setSelected={onChange} list={listRoles} disabled={isRoleDisabled} />}
                />
                {isRoleDisabled && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t("user.role_locked")}</p>}
            </div>
            <div className="mb-3">
                <label className="mb-1 block text-black dark:text-white">
                    {t("user.name")}
                </label>
                <input
                    type="text"
                    placeholder={t("placeholder.name")}
                    className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    {...register("name", {required: t("validation.required")})}
                    disabled={isRoleDisabled}
                    readOnly={isRoleDisabled}
                />
                { errors.name && <p className="mt-1 text-rose-600">{errors.name.message}</p>}
            </div>
            <div className="mb-3">
                <label className="mb-1 block text-black dark:text-white">
                    {t("user.email")}
                </label>
                <input
                    type="email"
                    placeholder={t("placeholder.email")}
                    className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    {...register("email", {
                        required: t("validation.required"),
                        pattern: {
                            value: emailRegex,
                            message: t("validation.email_valid")
                        }
                    })}
                    disabled={isEdit}
                    readOnly={isEdit}
                />
                { errors.email && <p className="mt-1 text-rose-600">{errors.email.message}</p>}
            </div>
            <div className="mb-3">
                <label className="mb-1 block dark:text-white">
                    {t("user.status")}
                </label>
                <Controller
                    name="status"
                    control={control}
                    defaultValue={listStatus[1].value}
                    render={({field: {onChange, value}}) => <DropDownList selected={value} setSelected={onChange} list={listStatus} disabled={isRoleDisabled} />}
                />
                {isRoleDisabled && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t("user.status_locked")}</p>}
            </div>

            {/* Discord Integration Section - Admin/SuperAdmin only */}
            {auth.role <= 2 && (
                <>
                    <div className="mt-7 mb-5 border dark:border-form-strokedark"></div>
                    <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Discord Integration (Optional)
                        </p>
                    </div>
                    <div className="mb-3">
                        <label className="mb-1 block text-black dark:text-white">
                            Discord User ID
                        </label>
                        <input
                            type="text"
                            placeholder="123456789012345678"
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            {...register("discord_user_id", {
                                pattern: {
                                    value: /^[0-9]{17,20}$/,
                                    message: "Discord User ID must be 17-20 digits"
                                }
                            })}
                            disabled={isRoleDisabled}
                            readOnly={isRoleDisabled}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Enable this user to access API via Discord bot. Leave blank to disable API access.
                        </p>
                        {errors.discord_user_id && <p className="mt-1 text-rose-600">{errors.discord_user_id.message}</p>}
                    </div>
                    <div className="mb-3">
                        {(() => {
                            const discordId = watch("discord_user_id") || ""
                            const isValidDiscordId = /^[0-9]{17,20}$/.test(discordId)
                            const isApiOnlyDisabled = isRoleDisabled || !isValidDiscordId

                            return (
                                <>
                                    <label className={`flex items-center ${isApiOnlyDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            className="sr-only"
                                            {...register("api_only")}
                                            disabled={isApiOnlyDisabled}
                                        />
                                        <div className="relative">
                                            <div className="block h-5 w-9 rounded-full bg-meta-9 dark:bg-[#5A616B]"></div>
                                            <div className={`absolute left-1 top-1 h-3 w-3 rounded-full bg-white transition ${watch("api_only") && "!right-1 !translate-x-full !bg-primary dark:!bg-white"}`}></div>
                                        </div>
                                        <div className="ml-3 text-sm font-medium text-black dark:text-white">
                                            API Only Access
                                        </div>
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-12">
                                        {!isValidDiscordId
                                            ? "Enter a valid Discord User ID to enable API Only Access"
                                            : "User can ONLY access via Discord bot (cannot login to web app)"}
                                    </p>
                                </>
                            )
                        })()}
                    </div>
                </>
            )}

            <div className="mt-7 mb-5 border dark:border-form-strokedark"></div>
            <div>
                <p className="dark:text-white mb-3">{t("user.skip_password")}</p>
            </div>
            <div className="mb-3">
                <label className="mb-1 block text-black dark:text-white">
                    {t("user.password")}
                </label>
                <input
                    type="password"
                    placeholder={t("placeholder.password")}
                    className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    {...register("password", {required: isEdit ? false : t("validation.required")})}
                    disabled={isRoleDisabled}
                    readOnly={isRoleDisabled}
                />
                { errors.password && <p className="mt-1 text-rose-600">{errors.password.message}</p>}
            </div>
            <div className="mb-3">
                <label className="mb-1 block text-black dark:text-white">
                    {t("user.retype_password")}
                </label>
                <input
                    type="password"
                    placeholder={t("placeholder.password")}
                    className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    {...register("retype_password", {
                        required: isEdit && watch("password") === "" ? false : t("validation.required"),
                        validate: (val: string) => {
                            if (watch("password") !== "" && watch('password') != val) {
                                return t("validation.password_not_match");
                            }
                            return true;
                        },
                    })}
                    disabled={isRoleDisabled}
                    readOnly={isRoleDisabled}
                />
                { errors.retype_password && <p className="mt-1 text-rose-600">{errors.retype_password.message}</p>}
            </div>
        </>
    )
}
